import axios from "axios";
import { getDeviceId } from "./device";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const API_V1 = `${BACKEND_URL}/api/v1`;

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json", "X-Device-Id": getDeviceId() },
  withCredentials: true,  // iter-36: send session_token cookie so
                          // /sessions is scoped to the signed-in user.
});

export const apiV1 = axios.create({
  baseURL: API_V1,
  headers: { "Content-Type": "application/json", "X-Device-Id": getDeviceId() },
  withCredentials: true,
});

export const Sessions = {
  list: () => api.get("/sessions").then((r) => r.data),
  get: (id) => api.get(`/sessions/${id}`).then((r) => r.data),
  create: (payload) => api.post("/sessions", payload).then((r) => r.data),
  update: (id, payload) => api.patch(`/sessions/${id}`, payload).then((r) => r.data),
  remove: (id) => api.delete(`/sessions/${id}`).then((r) => r.data),
};

// ============ Billing (iter-17) ============
// Email-only identity. Persisted in localStorage so returning users
// (paid or unpaid) can be auto-checked on app boot. Never used for auth
// gating beyond the sync feature itself.
const BILLING_EMAIL_KEY = "ssc:billingEmail";

export const BillingEmail = {
  get: () => (localStorage.getItem(BILLING_EMAIL_KEY) || "").trim().toLowerCase(),
  set: (email) => localStorage.setItem(BILLING_EMAIL_KEY, (email || "").trim().toLowerCase()),
  clear: () => localStorage.removeItem(BILLING_EMAIL_KEY),
};

export const Billing = {
  createCheckoutSession: ({ email, plan_id = "starter" }) =>
    api.post("/billing/checkout/session", {
      email,
      plan_id,
      origin_url: window.location.origin,
    }).then((r) => r.data),
  checkoutStatus: (sessionId) =>
    api.get(`/billing/checkout/status/${sessionId}`).then((r) => r.data),
  status: (email) =>
    api.get(`/billing/status`, { params: { email } }).then((r) => r.data),
  // iter-24: Stripe Customer Portal — buyers self-serve manage subscription
  portalSession: (email) =>
    api.post("/billing/portal/session", {
      email,
      return_url: `${window.location.origin}/sync`,
    }).then((r) => r.data),
};

export const Contact = {
  send: (body) => apiV1.post("/contact", body).then((r) => r.data),
};

export const Telemetry = {
  get: (sessionId) => apiV1.get(`/sessions/${sessionId}/telemetry`).then((r) => r.data),
  import: (sessionId, filename, content) =>
    apiV1.post(`/sessions/${sessionId}/telemetry`, { filename, content }).then((r) => r.data),
  apply: (sessionId, body) => apiV1.post(`/sessions/${sessionId}/telemetry/apply`, body).then((r) => r.data),
  remove: (sessionId) => apiV1.delete(`/sessions/${sessionId}/telemetry`).then((r) => r.data),
  geojson: (sessionId) => apiV1.get(`/sessions/${sessionId}/geojson`).then((r) => r.data),
};

export const Survey = {
  get: (sessionId) => apiV1.get(`/sessions/${sessionId}/survey`).then((r) => r.data),
  kml: (sessionId) => apiV1.get(`/sessions/${sessionId}/kml`, { responseType: "text" }).then((r) => r.data),
  report: (sessionId, client, notes) =>
    apiV1.get(`/sessions/${sessionId}/report.pdf`, { params: { client, notes }, responseType: "blob", timeout: 60000 }).then((r) => r.data),
};

export const Geo = {
  search: (body) => apiV1.post("/geo/search", body).then((r) => r.data),
  geocode: (q) => apiV1.get("/geo/geocode", { params: { q } }).then((r) => r.data),
};

export const UploadKeys = {
  list: () => apiV1.get("/upload-keys").then((r) => r.data),
  create: (label) => apiV1.post("/upload-keys", { label }).then((r) => r.data),
  revoke: (id) => apiV1.delete(`/upload-keys/${id}`).then((r) => r.data),
  status: () => apiV1.get("/watcher/status").then((r) => r.data),
  scriptUrl: `${API_V1}/watcher/script`,
};

// Original bytes for any photo (inline or large-file storage).
export const fetchPhotoBlob = (photoId) =>
  apiV1.get(`/photos/${photoId}/file`, { responseType: "blob" }).then((r) => r.data);

export const saveBlob = (blob, filename) => {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
};

// Browser-displayable source: inline only for web formats, else generated thumbnails (DNG etc.).
export const photoSrc = (p, size = "full") => {
  if (!p) return null;
  const inline = /^image\/(jpeg|png|webp)/.test(p.content_type || "image/jpeg") ? p.image_data : null;
  return size === "preview" ? p.thumbnails?.preview || inline : inline || p.thumbnails?.full;
};

export const Photos = {
  list: (sessionId) => api.get(`/sessions/${sessionId}/photos`).then((r) => r.data),
  get: (sessionId, photoId) =>
    api.get(`/sessions/${sessionId}/photos/${photoId}`).then((r) => r.data),
  create: (sessionId, payload) =>
    api.post(`/sessions/${sessionId}/photos`, payload).then((r) => r.data),
  remove: (sessionId, photoId) =>
    api.delete(`/sessions/${sessionId}/photos/${photoId}`).then((r) => r.data),
  patchDisplay: (photoId, patch) =>
    apiV1.patch(`/photos/${photoId}/display`, patch).then((r) => r.data),
};

export const Sync = {
  getConfig: () => apiV1.get("/sync-config").then((r) => r.data),
  saveConfig: (payload) => apiV1.post("/sync-config", payload).then((r) => r.data),
  validateUrl: (base_url, force = false) =>
    apiV1.post("/sync-config/validate", { base_url, force }).then((r) => r.data),
  sparkline: (base_url, hours = 24) =>
    apiV1
      .get(`/sync-config/sparkline?base_url=${encodeURIComponent(base_url)}&hours=${hours}`)
      .then((r) => r.data),
  getIngestContract: () => apiV1.get("/ingest-contract").then((r) => r.data),
  handshake: ({ base_url, api_key } = {}) =>
    apiV1.post("/sync/handshake", { base_url, api_key }).then((r) => r.data),
  forwardPhoto: (sessionId, photoId, payload = {}, idempotencyKey) => {
    const headers = {};
    if (idempotencyKey) headers["Idempotency-Key"] = idempotencyKey;
    // iter-17: attach the billing email so the soft paywall can identify
    // the caller. Empty/absent is fine when the paywall is OFF.
    const billingEmail = BillingEmail.get();
    if (billingEmail) headers["X-User-Email"] = billingEmail;
    return apiV1
      .post(`/sessions/${sessionId}/photos/${photoId}/forward`, payload, { headers })
      .then((r) => r.data);
  },
  // Legacy summary ping (kept for the lightweight "Send Session Summary" button)
  syncSession: (sessionId, payload = {}) =>
    api.post(`/sessions/${sessionId}/sync`, payload).then((r) => r.data),
  // Legacy per-photo sync (kept for backwards compat — v1 forwardPhoto preferred)
  syncPhoto: (sessionId, photoId, payload = {}) =>
    api.post(`/sessions/${sessionId}/photos/${photoId}/sync`, payload).then((r) => r.data),
  listReceipts: (sessionId) =>
    apiV1.get(`/sessions/${sessionId}/sync-receipts?limit=50`).then((r) => r.data),
  clearReceipts: (sessionId) =>
    apiV1.delete(`/sessions/${sessionId}/sync-receipts`).then((r) => r.data),
};

export const Webhooks = {
  list: () => apiV1.get("/webhooks").then((r) => r.data),
  create: (payload) => apiV1.post("/webhooks", payload).then((r) => r.data),
  remove: (id) => apiV1.delete(`/webhooks/${id}`).then((r) => r.data),
  recentDeliveries: (limit = 20, opts = {}) => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (opts.webhook_id) params.set("webhook_id", opts.webhook_id);
    if (opts.status) params.set("status", opts.status);
    return apiV1.get(`/webhook-deliveries?${params.toString()}`).then((r) => r.data);
  },
  eventCatalogue: () => apiV1.get("/event-catalogue").then((r) => r.data),
  eventSources: (event, limit = 20) => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (event) params.set("event", event);
    return apiV1.get(`/event-sources?${params.toString()}`).then((r) => r.data);
  },
  fireTest: (webhookId, payload) =>
    apiV1.post(`/webhooks/${webhookId}/fire-test`, payload).then((r) => r.data),
  replayDelivery: (deliveryId) =>
    apiV1.post(`/webhook-deliveries/${deliveryId}/replay`).then((r) => r.data),
};

// Admin/ops cockpit — all calls require X-Admin-Token header. Token is held
// in localStorage and applied per-call (NOT in a global axios default) so a
// stale or wrong token doesn't poison every other request.
const ADMIN_TOKEN_KEY = "ssc:adminToken";
export const AdminToken = {
  get: () => (typeof localStorage !== "undefined" ? localStorage.getItem(ADMIN_TOKEN_KEY) : null) || "",
  set: (t) => {
    if (typeof localStorage !== "undefined") {
      if (t) localStorage.setItem(ADMIN_TOKEN_KEY, t);
      else localStorage.removeItem(ADMIN_TOKEN_KEY);
    }
  },
};

const adminHeaders = () => {
  const t = AdminToken.get();
  return t ? { "X-Admin-Token": t } : {};
};

export const Admin = {
  secretsWhence: () => apiV1.get("/admin/secrets/whence", { headers: adminHeaders() }).then((r) => r.data),
  listContact: (status) =>
    apiV1.get("/admin/contact", { params: { limit: 200, status: status || undefined }, headers: adminHeaders() }).then((r) => r.data),
  setContactStatus: (id, status) =>
    apiV1.post(`/admin/contact/${id}/status`, null, { params: { status }, headers: adminHeaders() }).then((r) => r.data),
  listDeliveries: (opts = {}) => {
    const params = new URLSearchParams();
    if (opts.status) params.set("status", opts.status);
    if (opts.webhook_id) params.set("webhook_id", opts.webhook_id);
    if (opts.event) params.set("event", opts.event);
    if (opts.min_age_minutes != null) params.set("min_age_minutes", String(opts.min_age_minutes));
    if (opts.max_age_minutes != null) params.set("max_age_minutes", String(opts.max_age_minutes));
    if (opts.limit) params.set("limit", String(opts.limit));
    return apiV1
      .get(`/admin/deliveries?${params.toString()}`, { headers: adminHeaders() })
      .then((r) => r.data);
  },
  inspectDelivery: (id) =>
    apiV1.get(`/admin/deliveries/${id}`, { headers: adminHeaders() }).then((r) => r.data),
  sloPartners: (window_hours = 24) =>
    apiV1
      .get(`/admin/slo/partners?window_hours=${window_hours}`, { headers: adminHeaders() })
      .then((r) => r.data),
  sloPartnerDrilldown: (webhook_id, window_hours = 24) =>
    apiV1
      .get(`/admin/slo/partners/${webhook_id}?window_hours=${window_hours}`, {
        headers: adminHeaders(),
      })
      .then((r) => r.data),
  // Returns the export URL operators paste/click for an authenticated download.
  // We can't simply navigate to a URL with a custom header, so the export UI
  // performs a fetch with the header, then converts the blob to a download.
  exportDeliveriesUrl: (opts = {}) => {
    const params = new URLSearchParams();
    if (opts.status) params.set("status", opts.status);
    if (opts.webhook_id) params.set("webhook_id", opts.webhook_id);
    if (opts.event) params.set("event", opts.event);
    if (opts.min_age_minutes != null) params.set("min_age_minutes", String(opts.min_age_minutes));
    if (opts.max_age_minutes != null) params.set("max_age_minutes", String(opts.max_age_minutes));
    if (opts.limit) params.set("limit", String(opts.limit));
    if (opts.format) params.set("format", opts.format);
    return `${API_V1}/admin/deliveries/export?${params.toString()}`;
  },
  exportDeliveries: async (opts = {}) => {
    // Build a real Blob download (preserves Content-Disposition filename when
    // possible) by fetching with admin headers.
    const url = Admin.exportDeliveriesUrl(opts);
    const resp = await fetch(url, { headers: adminHeaders() });
    if (!resp.ok) {
      const text = await resp.text().catch(() => "");
      throw new Error(`Export failed (HTTP ${resp.status}): ${text.slice(0, 200)}`);
    }
    const blob = await resp.blob();
    const cd = resp.headers.get("content-disposition") || "";
    const m = cd.match(/filename="([^"]+)"/i);
    const fname = m ? m[1] : `ssc-export.${opts.format || "csv"}`;
    return { blob, filename: fname };
  },
  replayDelivery: (id) =>
    apiV1.post(`/admin/deliveries/${id}/replay`, null, { headers: adminHeaders() }).then((r) => r.data),

  // Alerts (iter-15c, Tier 2)
  Alerts: {
    listRules: () => apiV1.get("/admin/alerts/rules", { headers: adminHeaders() }).then((r) => r.data),
    createRule: (payload) =>
      apiV1.post("/admin/alerts/rules", payload, { headers: adminHeaders() }).then((r) => r.data),
    patchRule: (id, patch) =>
      apiV1.patch(`/admin/alerts/rules/${id}`, patch, { headers: adminHeaders() }).then((r) => r.data),
    deleteRule: (id) =>
      apiV1.delete(`/admin/alerts/rules/${id}`, { headers: adminHeaders() }).then((r) => r.data),
    listState: () => apiV1.get("/admin/alerts/state", { headers: adminHeaders() }).then((r) => r.data),
    listDeliveries: (opts = {}) => {
      const params = new URLSearchParams();
      if (opts.rule_id) params.set("rule_id", opts.rule_id);
      if (opts.status) params.set("status", opts.status);
      if (opts.limit) params.set("limit", String(opts.limit));
      return apiV1
        .get(`/admin/alerts/deliveries?${params.toString()}`, { headers: adminHeaders() })
        .then((r) => r.data);
    },
    fireTest: (ruleId) =>
      apiV1.post(`/admin/alerts/rules/${ruleId}/fire-test`, null, { headers: adminHeaders() })
        .then((r) => r.data),
    evaluate: () =>
      apiV1.post("/admin/alerts/evaluate", null, { headers: adminHeaders() }).then((r) => r.data),
    replayDelivery: (id) =>
      apiV1.post(`/admin/alerts/deliveries/${id}/replay`, null, { headers: adminHeaders() })
        .then((r) => r.data),
    // iter-15h — decision trace log ("Explain this alert")
    listTraces: (opts = {}) => {
      const params = new URLSearchParams();
      if (opts.rule_id) params.set("rule_id", opts.rule_id);
      if (opts.reason) params.set("reason", opts.reason);
      if (opts.decision) params.set("decision", opts.decision);
      if (opts.limit) params.set("limit", String(opts.limit));
      return apiV1
        .get(`/admin/alerts/traces?${params.toString()}`, { headers: adminHeaders() })
        .then((r) => r.data);
    },
    // iter-15f — maintenance windows (transition-layer suppression filter)
    listMaintenance: () =>
      apiV1.get("/admin/alerts/maintenance", { headers: adminHeaders() }).then((r) => r.data),
    createMaintenance: (payload) =>
      apiV1.post("/admin/alerts/maintenance", payload, { headers: adminHeaders() }).then((r) => r.data),
    deleteMaintenance: (id) =>
      apiV1.delete(`/admin/alerts/maintenance/${id}`, { headers: adminHeaders() }).then((r) => r.data),
  },

  // iter-21 — Billing Ops panel
  // NOTE: these live on `/api/admin/billing/*` (NOT /api/v1/) because the
  // billing router is mounted at /api in server.py — see billing.py docstring.
  Billing: {
    diagnostics: () =>
      api.get("/admin/billing/diagnostics", { headers: adminHeaders() }).then((r) => r.data),
    webhookHits: (limit = 50) =>
      api.get(`/admin/billing/webhook-hits?limit=${limit}`, { headers: adminHeaders() })
        .then((r) => r.data),
    grant: (email, note) =>
      api.post("/admin/billing/grant", { email, note }, { headers: adminHeaders() })
        .then((r) => r.data),
    // iter-29 — deploy readiness diagnostic
    deployReadiness: () =>
      api.get("/admin/billing/deploy-readiness", { headers: adminHeaders() })
        .then((r) => r.data),
    // iter-22 — runtime secret overrides (when deploy platform blocks env var add)
    setRuntimeSecret: (name, value) =>
      api.post("/admin/billing/runtime-secrets", { name, value }, { headers: adminHeaders() })
        .then((r) => r.data),
    clearRuntimeSecret: (name) =>
      api.delete(`/admin/billing/runtime-secrets/${encodeURIComponent(name)}`, { headers: adminHeaders() })
        .then((r) => r.data),
  },
};
